import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Download, FileBarChart } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { api, API } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

const selectClass = "h-10 rounded-md border border-input bg-background px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export default function ReportsPage() {
  const { user } = useAuth();
  const isStaff = user?.role !== "client_user";
  const [params, setParams] = useSearchParams();
  const [reports, setReports] = useState([]);
  const [clients, setClients] = useState([]);
  const [result, setResult] = useState({ headers: [], rows: [] });
  const [loading, setLoading] = useState(true);

  const selectedId = params.get("report") || "inventory-by-client";
  const clientId = params.get("client") || "";
  const frm = params.get("from") || "";
  const to = params.get("to") || "";

  const [fromInput, setFromInput] = useState(frm);
  const [toInput, setToInput] = useState(to);
  const today = new Date().toISOString().slice(0, 10);
  useEffect(() => { setFromInput(frm); setToInput(to); }, [frm, to]);
  const dateRangeError = fromInput && toInput && fromInput > toInput
    ? "From Date cannot be later than To Date."
    : (fromInput > today || toInput > today
        ? "Dates cannot be in the future."
        : "");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [rs, cs] = await Promise.all([
        api.get("/reports"),
        isStaff ? api.get("/clients") : Promise.resolve({ data: [] }),
      ]);
      setReports(rs.data);
      setClients((cs.data || []).filter((c) => !c.is_archived));
      const r = await api.get(`/reports/${selectedId}/run`, { params: { client_id: clientId || undefined, frm: frm || undefined, to: to || undefined } });
      setResult(r.data);
    } finally { setLoading(false); }
  }, [selectedId, clientId, frm, to, isStaff]);

  useEffect(() => { load(); }, [load]);

  function handleSubmit(e) {
    e.preventDefault();
    if (dateRangeError) return;
    const fd = new FormData(e.target);
    const np = new URLSearchParams();
    if (fd.get("report")) np.set("report", fd.get("report"));
    if (fd.get("client")) np.set("client", fd.get("client"));
    if (fd.get("from")) np.set("from", fd.get("from"));
    if (fd.get("to")) np.set("to", fd.get("to"));
    setParams(np);
  }

  async function downloadCsv() {
    if (dateRangeError) return;
    const token = localStorage.getItem("dolphin_token");
    const u = new URL(`${API}/reports/${selectedId}/export`);
    if (clientId) u.searchParams.set("client_id", clientId);
    if (frm) u.searchParams.set("frm", frm);
    if (to) u.searchParams.set("to", to);
    const resp = await fetch(u.toString(), { headers: { Authorization: `Bearer ${token}` } });
    const blob = await resp.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `${selectedId}-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    window.URL.revokeObjectURL(url);
  }

  const selected = reports.find((r) => r.id === selectedId);
  const previewRows = result.rows.slice(0, 50);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold tracking-tight">Reports</h2>
        <p className="text-muted-foreground">{selected?.description || "Pick a report to view"}</p>
      </div>
      <form onSubmit={handleSubmit} className="flex flex-wrap items-end gap-3">
        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground">Report</label>
          <select name="report" defaultValue={selectedId} className={selectClass}>
            {reports.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
        </div>
        {isStaff && (
          <div className="space-y-1">
            <label className="text-xs font-medium text-muted-foreground">Organization</label>
            <select name="client" defaultValue={clientId} className={selectClass}>
              <option value="">All organizations</option>
              {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </div>
        )}
        <div className="space-y-1"><label className="text-xs font-medium text-muted-foreground">From</label>
          <input
            type="date"
            name="from"
            value={fromInput}
            max={toInput && toInput < today ? toInput : today}
            onChange={(e) => setFromInput(e.target.value)}
            className={selectClass + (dateRangeError ? " border-rose-400 focus-visible:ring-rose-400" : "")}
            data-testid="report-from-date"
          />
        </div>
        <div className="space-y-1"><label className="text-xs font-medium text-muted-foreground">To</label>
          <input
            type="date"
            name="to"
            value={toInput}
            min={fromInput || undefined}
            max={today}
            onChange={(e) => setToInput(e.target.value)}
            className={selectClass + (dateRangeError ? " border-rose-400 focus-visible:ring-rose-400" : "")}
            data-testid="report-to-date"
          />
        </div>
        <Button type="submit" variant="outline" disabled={!!dateRangeError} data-testid="run-report-btn"><FileBarChart className="h-4 w-4" /> Run Report</Button>
        <Button type="button" onClick={downloadCsv} disabled={!!dateRangeError}><Download className="h-4 w-4" /> Export CSV</Button>
      </form>
      {dateRangeError && (
        <p className="-mt-3 text-sm font-medium text-rose-600" data-testid="date-range-error">{dateRangeError}</p>
      )}
      <Card>
        <CardContent className="p-0">
          {loading ? <p className="p-10 text-center text-muted-foreground">Loading…</p> :
            previewRows.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b text-left text-muted-foreground">{result.headers.map((h) => <th key={h} className="whitespace-nowrap px-4 py-3 font-medium">{h}</th>)}</tr></thead>
                  <tbody>
                    {previewRows.map((row, i) => (
                      <tr key={i} className="border-b last:border-0 hover:bg-slate-50">
                        {row.map((cell, j) => <td key={j} className="whitespace-nowrap px-4 py-2.5">{cell ?? "—"}</td>)}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : <p className="p-10 text-center text-muted-foreground">No data for this report with the current filters.</p>
          }
        </CardContent>
      </Card>
      <p className="text-sm text-muted-foreground">Preview shows the first 50 rows ({result.rows.length} total). The CSV export contains all rows.</p>
    </div>
  );
}
